/**
 * Tags Field Type - Common
 *
 * Field-type configuration and the shared array→strings parsing helpers used by
 * both the inline (inline/tags.tsx) and modal (modal/tags.tsx) renderers.
 *
 * Automatically handles arrays of:
 * - Strings: ['a', 'b', 'c'] → [a] [b] [c]
 * - Objects with `name`: [{name: 'a'}, {name: 'b'}] → [a] [b]
 * - Objects with `label`: [{label: 'a'}, {label: 'b'}] → [a] [b]
 */

import type { FieldTypeConfig } from '../../components/types';
import { L } from './columnWidths';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Convert an array value to an array of strings for tag display
 * Handles arrays of strings, objects with `name`, or objects with `label`
 */
export const parseTagsValue = (value: unknown): string[] => {
  if (!value) return [];

  // If already a string, split by comma (for backward compatibility)
  if (typeof value === 'string') {
    return value
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }

  // If not an array, convert to single-item array
  if (!Array.isArray(value)) return [String(value)];

  // Empty array
  if (value.length === 0) return [];

  // Map array items to strings
  return value
    .map(item => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object') {
        // Try `name` first, then `label`
        return (
          (item as { name?: string; label?: string }).name ||
          (item as { name?: string; label?: string }).label ||
          ''
        );
      }
      return String(item ?? '');
    })
    .filter(Boolean);
};

/**
 * Format tags as comma-separated string (for tooltip display)
 */
export const formatTagsTooltip = (value: unknown): string => {
  return parseTagsValue(value).join(', ');
};

// ============================================================================
// Field Type Configuration
// ============================================================================

/**
 * Tags field type configuration
 * Read-only display for tag values
 */
export const tagsFieldType: FieldTypeConfig = {
  placeholder: '',
  ...L,
};
